import { Component, OnInit, OnDestroy, ChangeDetectionStrategy } from '@angular/core';
import { Router, RouterLink } from '@angular/router';
import { UIComponentsModule } from 'src/app/shared/ui-components/ui-components.module';
import { CommonModule } from '@angular/common';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { ReactiveFormsModule } from '@angular/forms';
import { DailyService } from '../logs/daily/daily.service';
import { BaselineService } from '../logs/general/baseline.service';
import {
  userFoodEntry,
  userActivityEntry,
  userMobilityEntry,
  baselineCategory,
  baselineQuestion,
  UserFlightEntry,
  userProductEntry,
  userBaselineEntry,
  GroupMembership,
} from 'src/app/shared/models/interfaces';
import { isSameDay } from 'src/app/shared/utils/date';
import { Subscription } from 'rxjs';
import { DashboardChartComponent } from 'src/app/views/dashboard/dashboard-chart/dashboard-chart.component';
import { FormBuilder, FormGroup, Validators } from '@angular/forms';
import { UserService } from 'src/app/core/services/user/user.service';
import { SnackbarService } from 'src/app/core/services/snackbar/snackbar.service';
import { AuthService } from 'src/app/core/auth/auth.service';
import { isInFuture, isToday, isInGroupTimePeriod } from 'src/app/shared/utils/date';

@Component({
  selector: 'app-dashboard',
  imports: [
    UIComponentsModule,
    RouterLink,
    CommonModule,
    MatTooltipModule,
    DashboardChartComponent,
    MatFormFieldModule,
    MatInputModule,
    ReactiveFormsModule,
  ],
  templateUrl: './dashboard.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './dashboard.component.scss',
})
export class DashboardComponent implements OnInit, OnDestroy {
  // Daily logs data
  displayedDates: Date[] = [];
  userFoodEntries: userFoodEntry[] = [];
  userActivityEntries: userActivityEntry[] = [];
  userMobilityEntries: userMobilityEntry[] = [];

  // Baseline logs data
  categories: baselineCategory[] = [];
  questions: baselineQuestion[] = [];
  userBaselineEntries: userBaselineEntry[] = [];
  userProductEntries: userProductEntry[] = [];
  userFlightEntries: UserFlightEntry[] = [];

  // Groups data
  groupCodeForm: FormGroup;
  groupMemberships: GroupMembership[] = [];
  hasSkippedGroupJoining: boolean = false;

  private subscriptions: Subscription = new Subscription();

  constructor(
    private fb: FormBuilder,
    private userService: UserService,
    private router: Router,
    private dailyService: DailyService,
    private baselineService: BaselineService,
    private snackBarService: SnackbarService,
    private authService: AuthService
  ) {
    this.initializeLastSevenDaysRange();

    // Form for joining a group by code
    this.groupCodeForm = this.fb.group({
      groupCode: ['', [Validators.required]],
    });
  }

  ngOnInit(): void {
    this.loadDailyData();
    this.loadBaselineData();
    this.loadUserGroupInfo();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  initializeLastSevenDaysRange() {
    const today = new Date();
    for (let i = 6; i >= 0; i--) {
      const date = new Date(today);
      date.setDate(today.getDate() - i);
      this.displayedDates.push(date);
    }
  }

  loadUserGroupInfo() {
    this.subscriptions.add(
      this.userService.getGroupsForUser().subscribe({
        next: (groupMemberships: GroupMembership[]) => {
          this.groupMemberships = groupMemberships;
        },
        error: () => {
          console.error('Failed to load groups.');
        },
      })
    );
    this.subscriptions.add(
      this.authService.hasUserSkippedGroupJoining().subscribe((skipped) => {
        this.hasSkippedGroupJoining = skipped;
      })
    );
  }

  loadDailyData() {
    this.subscriptions.add(
      this.dailyService.userFoodEntries$.subscribe((entries) => {
        this.userFoodEntries = entries;
      })
    );

    this.subscriptions.add(
      this.dailyService.userActivityEntries$.subscribe((entries) => {
        this.userActivityEntries = entries;
      })
    );

    this.subscriptions.add(
      this.dailyService.userMobilityEntries$.subscribe((entries) => {
        this.userMobilityEntries = entries;
      })
    );
    this.dailyService.loadAllData();
  }

  loadBaselineData() {
    this.subscriptions.add(
      this.baselineService.getBaselineCategories().subscribe((categories) => {
        this.categories = categories;
      })
    );

    this.subscriptions.add(
      this.baselineService
        .getAllBaselineQuestionData()
        .subscribe((questions) => {
          this.questions = questions.filter(
            (q) => !q.hidden && q.baselineAnswers.length > 0
          );
        })
    );

    this.subscriptions.add(
      this.baselineService.getUserBaselineEntries().subscribe((entries) => {
        this.userBaselineEntries = entries;
      })
    );

    this.subscriptions.add(
      this.baselineService.getUserProductEntries().subscribe((entries) => {
        this.userProductEntries = entries;
      })
    );

    this.subscriptions.add(
      this.baselineService.getUserFlightEntries().subscribe((entries) => {
        this.userFlightEntries = entries;
      })
    );
  }

  hasEntries(date: Date): boolean {
    if (!date) return false;

    const hasFoodEntries = this.userFoodEntries.some(
      (entry) => isSameDay(entry.occurredAt, date) && entry.nrServings > 0
    );

    const hasActivityEntries = this.userActivityEntries.some(
      (entry) => isSameDay(entry.occurredAt, date) && entry.duration > 0
    );

    const hasMobilityEntries = this.userMobilityEntries.some(
      (entry) => isSameDay(entry.occurredAt, date) && entry.distance > 0
    );

    return hasFoodEntries || hasActivityEntries || hasMobilityEntries;
  }

  onDateSelected(date: Date | null) {
    if (date) {
      this.router.navigate(['/logs/daily'], {
        queryParams: { selectedDate: date.toISOString() },
      });
    } else {
      this.router.navigate(['/logs/daily'], {
        queryParams: { selectedDate: new Date().toISOString() },
      });
    }
  }

  // Baseline logs utility methods
  private getQuestionsForCategory(categoryId: number): baselineQuestion[] {
    return this.questions.filter(
      (question) => question.categoryId === categoryId
    );
  }

  getProgressForCategory(categoryId: number): number {
    const questionIds = this.getQuestionsForCategory(categoryId).map(
      (question) => question.id
    );
    const userEntryQuestionIds = this.userBaselineEntries.map(
      (entry) => entry.baselineQuestionId
    );
    const answeredQuestions = questionIds.filter((questionId) =>
      userEntryQuestionIds.includes(questionId)
    );

    const flightEntriesAnswered =
      categoryId === 1 && this.hasAnsweredFlightEntries() ? 1 : 0;
    const totalQuestions = questionIds.length + (categoryId === 1 ? 1 : 0);

    return totalQuestions > 0
      ? ((answeredQuestions.length + flightEntriesAnswered) / totalQuestions) *
          100
      : 0;
  }

  getAnsweredCount(categoryId: number): number {
    const questionIds = this.getQuestionsForCategory(categoryId).map(
      (q) => q.id
    );
    return (
      this.userBaselineEntries.filter((entry) =>
        questionIds.includes(entry.baselineQuestionId)
      ).length + (categoryId === 1 && this.hasAnsweredFlightEntries() ? 1 : 0)
    );
  }

  getTotalCount(categoryId: number): number {
    if (categoryId === 1) {
      return this.getQuestionsForCategory(categoryId).length + 1;
    }
    return this.getQuestionsForCategory(categoryId).length;
  }

  hasAnsweredFlightEntries(): boolean {
    return this.userFlightEntries.length > 0;
  }

  hasAnsweredDigitalizationEntries(): boolean {
    return this.userProductEntries.length > 0;
  }

  getDigitalizationProgress(): number {
    return this.hasAnsweredDigitalizationEntries() ? 100 : 0;
  }

  getGeneralLogsButtonLabel() {
    const answeredCount =
      this.userBaselineEntries.length +
      (this.hasAnsweredFlightEntries() ? 1 : 0) +
      (this.hasAnsweredDigitalizationEntries() ? 1 : 0);

    const totalCount =
      this.categories.reduce((total, category) => {
        return total + this.getTotalCount(category.id);
      }, 0) + 1; // +1 for digitalization

    return answeredCount === 0
      ? 'Start questionnaire'
      : answeredCount === totalCount
      ? 'Complete questionnaire'
      : 'Continue questionnaire';
  }

  onSubmitGroupCode(): void {
    if (this.groupCodeForm.valid) {
      const { groupCode } = this.groupCodeForm.value;
      this.userService.joinGroup(groupCode).subscribe({
        next: () => {
          this.snackBarService.showSuccessMessage('Joined group successfully!');
          // Optionally, refresh group list
          this.userService.getGroupsForUser().subscribe({
            next: (groupMemberships: GroupMembership[]) => {
              this.groupMemberships = groupMemberships;
            },
          });
          this.groupCodeForm.reset();
          this.groupCodeForm.get('groupCode')?.setErrors(null);
        },
        error: () => {
          this.snackBarService.showErrorMessage(
            'Failed to join group. Please check the group code and try again.'
          );
        },
      });
    }
  }

  onSkipGroupJoining(): void {
    this.userService.skipGroupJoining().subscribe({
      next: () => {
        this.hasSkippedGroupJoining = true;
        this.snackBarService.showSuccessMessage(
          'You can join a group later in your profile settings.'
        );
      },
      error: (e) => {
        console.log(e);
        this.snackBarService.showErrorMessage(
          'Failed to skip group joining. Please try again.'
        );
      },
    });
  }

  // Date utility methods, declare to use the imported utility functions in the template
  isInFuture = isInFuture;
  isToday = isToday;
  isInGroupTimePeriod = isInGroupTimePeriod;
}
